/**
 * Homepage with server-side data fetching (restores previous flow)
 */
import withStyles from '@mui/styles/withStyles'
import { WithRouterProps } from 'next/dist/client/with-router'
import { withRouter } from 'next/router'
import React from 'react'
import {
  GetAllClassInSchool,
  GetAllPaymentsPlanInSchool,
  GetCategoriesInSchool,
} from '../services/api-requests/api-request'
import { GetSchoolThemeFromHostname } from '../services/api-requests/school-theme-request'
import SchoolHomepage from '../components/SchoolHomepage'
import SchoolUserParentContainerComponent from '../containers/SchoolUser/SchoolUserParentContainerComponent'
import PageLoadingComponent from '../components/LoadingComponents/PageLoadingComponent'
import LiveChatExternalIntegrationDomComponent from '../components/ExternalIntegrations/LiveChatIntegrations/LiveChatExternalIntegrationDomComponent'
import SchoolMaterialThemeContainer from '../containers/SchoolMaterialThemeContainer'
import { useSchoolDetails } from '../hooks/useSchoolDetails'
import ErrorComponent from './_error'
import SchoolUserPageHead from '../components/SchoolUserPageHead'

const styles = () => ({})

interface IIndexPageProps extends WithRouterProps {
  classes: any // for material ui
  school_details: any
  currentUrl: string
  courses: any[]
  selectedCourseIdOrSlug?: string
  categories: any[]
  payment_plans: any[]
  error?: boolean
}
interface IIndexPageState {
  onClient: boolean
}

class IndexPage extends React.Component<IIndexPageProps, IIndexPageState> {
  constructor(props: IIndexPageProps) {
    super(props)
    this.state = {
      onClient: false,
    }
  }

  static async getSchoolTheme(hostname: string) {
    try {
      hostname =
        hostname === 'test.demi.com' ? 'demischool.pledre.school' : hostname // TODO: Remove line as it is for test

      const html = (await GetSchoolThemeFromHostname(hostname))?.data?.data
        ?.html
      return html
    } catch (err) {
      return undefined
    }
  }

  componentDidMount() {
    this.setState({ onClient: true })
    if (this.props.school_details?.load_dashboard_as_homepage) {
      this.props.router.replace('/dashboard')
    }
  }

  render() {
    const { classes } = this.props
    if (this.props?.error) {
      return <ErrorComponent statusCode={404} />
    }

    if (this.props.school_details?.load_dashboard_as_homepage) {
      return <PageLoadingComponent />
    }

    return (
      <>
        <SchoolUserPageHead schoolDetails={this.props.school_details} />
        <SchoolMaterialThemeContainer schoolDetails={this.props.school_details}>
          <>
            {this.props.school_details ? (
              <LiveChatExternalIntegrationDomComponent
                currentUrl={this.props.currentUrl}
                schoolDetails={this.props.school_details}
              />
            ) : (
              <div />
            )}
            <SchoolUserParentContainerComponent
              school_id={this.props.school_details._id}
              fb_pixel_id={this.props.school_details?.fb_pixels_id}
            >
              <div style={{ backgroundColor: '#fff' }} className={classes.root}>
                <SchoolHomepage
                  openCourseIdDetails={this.props.selectedCourseIdOrSlug}
                  courses={this.props.courses}
                  schoolDetails={this.props.school_details}
                  categories={this.props.categories}
                  payment_plans={this.props.payment_plans}
                />
              </div>
            </SchoolUserParentContainerComponent>
          </>
        </SchoolMaterialThemeContainer>
      </>
    )
  }
}

// getServerSideProps removed during migration; this page is now client-side only

const IndexWrapper: React.FC<any> = ({ classes, router }: any) => {
  const {
    error: schoolError,
    schoolDetails,
  } = useSchoolDetails()
  const [courses, setCourses] = React.useState<any[]>([])
  const [categories, setCategories] = React.useState<any[]>([])
  const [paymentPlans, setPaymentPlans] = React.useState<any[]>([])

  React.useEffect(() => {
    if (!schoolDetails?._id) {
      setCourses([])
      setCategories([])
      setPaymentPlans([])
      return
    }

    let active = true
    const fetch = async () => {
      try {
        const [coursesRes, categoriesRes, paymentPlansRes] = await Promise.all([
          GetAllClassInSchool(schoolDetails._id),
          GetCategoriesInSchool(schoolDetails._id),
          GetAllPaymentsPlanInSchool(schoolDetails._id),
        ])
        if (!active) return
        setCourses(coursesRes?.data?.data || [])
        setCategories(categoriesRes?.data?.data || [])
        setPaymentPlans(paymentPlansRes?.data?.data || [])
      } catch (err) {
        if (active) {
          console.error('Error fetching homepage data', err)
        }
      }
    }
    fetch().catch(err => {
      console.error('Error fetching homepage data', err)
    })

    return () => {
      active = false
    }
  }, [schoolDetails?._id])

  if (schoolError) {
    return <ErrorComponent statusCode={404} />
  }

  if (!schoolDetails) {
    return null
  }
  return (
    <IndexPage
      classes={classes}
      router={router}
      school_details={schoolDetails}
      currentUrl={
        typeof window !== 'undefined' ? window.location.href : ''
      }
      courses={courses}
      categories={categories}
      payment_plans={paymentPlans}
    />
  )
}

export default withRouter(withStyles(styles)(IndexWrapper))
